import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthProvider";

import StatsCard from "./StatsCard";
import CompactStatsCard from "./CompactStatsCard";
import Loader from "./Loader";
import { STUDENT_LEVELS } from "../utils";

import {
  Badge,
  Button,
  Card,
  Form,
  Tabs,
  Tab,
  ListGroup,
  Modal,
} from "react-bootstrap";
import { Clock, Wrench, BookOpen, CheckCircle, XIcon } from "lucide-react";
import { formatDistanceToNow, set } from "date-fns";
import { toast } from "react-toastify";

import {
  getAssignedTasksByUserId,
  updateTaskStatus,
} from "../services/taskService";
import { getClassesByUserId } from "../services/classService";
import Classes from "../pages/Classes";
import LearningMaterials from "./LearningMaterials";
import TaskDescriptionView from "./TaskDescriptionView";
import { getAdminActivities } from "../services/activityService";
import { getSkills } from "../services/skillService";
import { updateUser } from "../services/userService";
import {
  checkIn,
  checkOut,
  getTodayAttendance,
  getUserAttendance,
} from "../services/attendanceService";
import {
  getLeavesByUserId,
  getLeaveStats,
  applyForLeave,
  cancelLeave,
  LEAVE_TYPES,
  LEAVE_STATUS,
  calculateLeaveDays,
} from "../services/leaveService";
import {
  getProjectsByStudentId,
  PROJECT_STATUS,
  PROJECT_PRIORITY,
} from "../services/projectService";

export default function ReadonlyStudentDashboard({ student, show, onHide }) {
  const { user: currentUser } = useAuth();

  // Use the passed student or fallback to current user
  const user = student || currentUser;

  const [tasks, setTasks] = useState([]);
  const [classes, setClasses] = useState([]);
  const [activities, setActivities] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [leaveStats, setLeaveStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [allSkills, setAllSkills] = useState([]);
  const [userSkills, setUserSkills] = useState([]);

  const isReadonly = !!student; // If student is passed, it's readonly mode

  useEffect(() => {
    if (show && user) {
      fetchData();
    }
  }, [show, user]);

  const fetchData = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      await Promise.all([
        fetchTasks(),
        fetchClasses(),
        fetchActivities(),
        fetchAttendance(),
        fetchTodayAttendance(),
        fetchLeaves(),
        fetchProjects(),
        fetchSkills(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const fetchTasks = async () => {
    try {
      const data = await getAssignedTasksByUserId(user.id);
      setTasks(data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
    }
  };

  const fetchClasses = async () => {
    try {
      const data = await getClassesByUserId(user.id);
      setClasses(data);
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
    }
  };

  const fetchActivities = async () => {
    try {
      const data = await getAdminActivities();
      setActivities(data);
    } catch (error) {
      console.error("Error fetching activities:", error);
      setActivities([]);
    }
  };

  const fetchAttendance = async () => {
    try {
      const data = await getUserAttendance(user.id);
      setAttendance(data);
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setAttendance([]);
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      const data = await getTodayAttendance(user.id);
      setTodayAttendance(data);
    } catch (error) {
      console.error("Error fetching today's attendance:", error);
      setTodayAttendance(null);
    }
  };

  const fetchLeaves = async () => {
    try {
      const [leavesData, statsData] = await Promise.all([
        getLeavesByUserId(user.id),
        getLeaveStats(user.id),
      ]);
      setLeaves(leavesData);
      setLeaveStats(statsData);
    } catch (error) {
      console.error("Error fetching leaves:", error);
      setLeaves([]);
      setLeaveStats(null);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await getProjectsByStudentId(user.id);
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    }
  };

  const fetchSkills = async () => {
    try {
      const skills = await getSkills();
      setAllSkills(skills);

      // Get user's skills
      if (user.skills && Array.isArray(user.skills)) {
        setUserSkills(user.skills);
      } else if (user.skillIds && Array.isArray(user.skillIds)) {
        const studentSkills = skills.filter((skill) =>
          user.skillIds.includes(skill.id)
        );
        setUserSkills(studentSkills);
      } else {
        setUserSkills([]);
      }
    } catch (error) {
      console.error("Error fetching skills:", error);
      setAllSkills([]);
      setUserSkills([]);
    }
  };

  // Don't render if no user data
  if (!user) {
    return (
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Body className="text-center py-5">
          <p className="text-muted">No user data available</p>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Body>
      </Modal>
    );
  }

  if (loading) {
    return (
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Body className="text-center py-5">
          <Loader />
          <p className="mt-3">Loading student dashboard...</p>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="dark-theme-modal"
    >
      <Modal.Header closeButton className="bg-light dark:bg-gray-800">
        <Modal.Title className="text-dark dark:text-white">
          {isReadonly ? `${user?.name}'s Dashboard` : "My Dashboard"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: "80vh", overflowY: "auto" }}
        className="bg-white dark:bg-gray-900 text-dark dark:text-white"
      >
        {/* Student Profile Header */}
        <div className="mb-4 text-center">
          <div className="d-inline-block position-relative">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="rounded-circle"
                style={{ width: "80px", height: "80px", objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : (
              <div
                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                style={{
                  width: "80px",
                  height: "80px",
                  fontSize: "2rem",
                  display: user?.avatarUrl ? "none" : "flex",
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || "S"}
              </div>
            )}
          </div>
          <h5 className="mt-2 mb-1">{user?.name}</h5>
          <p className="text-muted mb-0">{user?.email}</p>
          <Badge bg={user?.isActive ? "success" : "danger"}>
            {user?.isActive ? "Active" : "Inactive"}
          </Badge>{" "}
          <Badge
            bg={
              user?.studentLevel === STUDENT_LEVELS.PAID_TRAINEE
                ? "success"
                : user?.studentLevel === STUDENT_LEVELS.INTERN
                ? "warning"
                : user?.studentLevel === STUDENT_LEVELS.EMPLOYEE
                ? "info"
                : "secondary"
            }
          >
            {user?.studentLevel?.replace("_", " ").toUpperCase() || "Free Trainee"}
          </Badge>
        </div>

        {/* Quick Stats */}
        <div className="row mb-4">
          <div className="col-md-4">
            <CompactStatsCard
              title="Tasks"
              total={tasks ? tasks.length : 0}
              icon="✅"
              color="primary"
              stats={[
                {
                  value: tasks
                    ? tasks.filter((t) => t.status === "completed").length
                    : 0,
                  label: "Done",
                  colorClass: "text-success",
                },
                {
                  value: tasks
                    ? tasks.filter((t) => t.status === "in_progress").length
                    : 0,
                  label: "Progress",
                  colorClass: "text-warning",
                },
                {
                  value: tasks
                    ? tasks.filter((t) => t.status === "pending").length
                    : 0,
                  label: "Pending",
                  colorClass: "text-danger",
                },
              ]}
            />
          </div>
          <div className="col-md-4">
            <CompactStatsCard
              title="Projects"
              total={projects ? projects.length : 0}
              icon="📂"
              color="info"
              stats={[
                {
                  value: projects
                    ? projects.filter((p) => p.status === "completed").length
                    : 0,
                  label: "Done",
                  colorClass: "text-success",
                },
                {
                  value: projects
                    ? projects.filter((p) => p.status === "in_progress").length
                    : 0,
                  label: "Active",
                  colorClass: "text-warning",
                },
                {
                  value: projects
                    ? projects.filter((p) => p.status === "pending").length
                    : 0,
                  label: "Pending",
                  colorClass: "text-danger",
                },
              ]}
            />
          </div>
          <div className="col-md-4">
            <CompactStatsCard
              title="Leaves"
              total={leaves ? leaves.length : 0}
              icon="🏖️"
              color="warning"
              stats={[
                {
                  value: leaves
                    ? leaves.filter((l) => l.status === "approved").length
                    : 0,
                  label: "Approved",
                  colorClass: "text-success",
                },
                {
                  value: leaves
                    ? leaves.filter((l) => l.status === "pending").length
                    : 0,
                  label: "Pending",
                  colorClass: "text-warning",
                },
                {
                  value: leaves
                    ? leaves.filter((l) => l.status === "rejected").length
                    : 0,
                  label: "Rejected",
                  colorClass: "text-danger",
                },
              ]}
            />
          </div>
          <div className="col-md-3"></div>
        </div>

        {/* Skills Section */}
        <div className="mb-4">
          <Card className="shadow">
            <Card.Body>
              <Card.Title>🛠️ Skills</Card.Title>
              <div className="d-flex flex-wrap gap-2">
                {userSkills && userSkills.length > 0 ? (
                  userSkills.map((skill, index) => (
                    <Badge
                      key={skill.id || index}
                      bg="primary"
                      className="d-flex align-items-center gap-1 p-2"
                    >
                      <span>{skill.name}</span>
                      {skill.level && (
                        <small className="opacity-75">({skill.level})</small>
                      )}
                    </Badge>
                  ))
                ) : (
                  <p className="text-muted">No skills added yet.</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs
          defaultActiveKey="tasks"
          id="readonly-dashboard-tabs"
          className="mb-3"
        >
          <Tab
            eventKey="tasks"
            title={`✅ Tasks (${tasks ? tasks.length : 0})`}
          >
            <Card className="shadow">
              <Card.Body>
                <Card.Title>✅ Tasks ({tasks ? tasks.length : 0})</Card.Title>
                {tasks && tasks.length === 0 ? (
                  <p className="text-muted text-center">No tasks assigned.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Task Name</th>
                          <th>Description</th>
                          <th>Project</th>
                          <th>Due Date</th>
                          <th>Status</th>
                          <th>Priority</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks &&
                          tasks.map((task) => (
                            <tr key={task.id}>
                              <td>{task.taskName}</td>
                              <td style={{ maxWidth: "300px" }}>
                                <div style={{ maxHeight: "112px", overflow: "hidden" }}>
                                  <TaskDescriptionView text={task.taskDescription} preview />
                                </div>
                              </td>
                              <td>
                                {task.projectName ? (
                                  <Badge bg="info">{task.projectName}</Badge>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {task.dueDate
                                  ? new Date(task.dueDate).toLocaleDateString()
                                  : "No due date"}
                              </td>
                              <td>
                                <Badge
                                  bg={
                                    task.status === "completed"
                                      ? "success"
                                      : task.status === "in_progress"
                                      ? "warning"
                                      : task.status === "overdue"
                                      ? "danger"
                                      : "secondary"
                                  }
                                >
                                  {task.status
                                    ?.replace("_", " ")
                                    .toUpperCase() || "PENDING"}
                                </Badge>
                              </td>
                              <td>
                                <Badge
                                  bg={
                                    task.priority === "high"
                                      ? "danger"
                                      : task.priority === "medium"
                                      ? "warning"
                                      : "info"
                                  }
                                >
                                  {task.priority?.toUpperCase() || "LOW"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab
            eventKey="projects"
            title={`📂 Projects (${projects ? projects.length : 0})`}
          >
            <Card className="shadow">
              <Card.Body>
                <Card.Title>
                  📂 Projects ({projects ? projects.length : 0})
                </Card.Title>
                {!projects || projects.length === 0 ? (
                  <p className="text-muted text-center">
                    No projects assigned.
                  </p>
                ) : (
                  <div className="row">
                    {projects.map((project) => (
                      <div key={project.id} className="col-md-6 mb-3">
                        <Card className="h-100">
                          <Card.Body>
                            <Card.Title className="h6">
                              {project.name}
                            </Card.Title>
                            <p className="text-muted small">
                              {project.description}
                            </p>
                            <div className="mb-2">
                              <Badge
                                bg={
                                  project.status === "completed"
                                    ? "success"
                                    : project.status === "in_progress"
                                    ? "warning"
                                    : project.status === "review"
                                    ? "info"
                                    : "secondary"
                                }
                              >
                                {project.status
                                  ?.replace("_", " ")
                                  .toUpperCase() || "PLANNING"}
                              </Badge>{" "}
                              <Badge
                                bg={
                                  project.priority === "high"
                                    ? "danger"
                                    : project.priority === "medium"
                                    ? "warning"
                                    : "info"
                                }
                              >
                                {project.priority?.toUpperCase() || "LOW"}
                              </Badge>
                            </div>
                            {project.startDate && (
                              <small className="text-muted">
                                Start:{" "}
                                {(() => {
                                  try {
                                    return project.startDate.toDate
                                      ? project.startDate
                                          .toDate()
                                          .toLocaleDateString()
                                      : new Date(
                                          project.startDate
                                        ).toLocaleDateString();
                                  } catch (error) {
                                    return "N/A";
                                  }
                                })()}
                              </small>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab
            eventKey="leaves"
            title={`🏖️ Leaves (${leaves ? leaves.length : 0})`}
          >
            <div className="row">
              {/* Leave Stats */}
              {leaveStats && (
                <div className="col-12 mb-3">
                  <Card className="shadow">
                    <Card.Body>
                      <Card.Title>📊 Leave Statistics</Card.Title>
                      <div className="row">
                        <div className="col-md-3">
                          <div className="text-center p-2 bg-success bg-opacity-10 rounded">
                            <div className="fw-bold text-success">
                              {leaveStats.approved || 0}
                            </div>
                            <small className="text-muted">Approved</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-center p-2 bg-warning bg-opacity-10 rounded">
                            <div className="fw-bold text-warning">
                              {leaveStats.pending || 0}
                            </div>
                            <small className="text-muted">Pending</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-center p-2 bg-danger bg-opacity-10 rounded">
                            <div className="fw-bold text-danger">
                              {leaveStats.rejected || 0}
                            </div>
                            <small className="text-muted">Rejected</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-center p-2 bg-info bg-opacity-10 rounded">
                            <div className="fw-bold text-info">
                              {leaves.length}
                            </div>
                            <small className="text-muted">Total</small>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              )}

              {/* Leave History */}
              <div className="col-12">
                <Card className="shadow">
                  <Card.Body>
                    <Card.Title>🗓️ Leave History</Card.Title>
                    {!leaves || leaves.length === 0 ? (
                      <p className="text-muted text-center">
                        No leave applications found.
                      </p>
                    ) : (
                      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                        {leaves.map((leave) => (
                          <Card key={leave.id} className="mb-2">
                            <Card.Body className="py-3">
                              <div className="row">
                                <div className="col-md-8">
                                  <h6 className="mb-1">
                                    <Badge bg="info">
                                      {leave.type?.toUpperCase()}
                                    </Badge>{" "}
                                    <Badge
                                      bg={
                                        leave.status === "approved"
                                          ? "success"
                                          : leave.status === "rejected"
                                          ? "danger"
                                          : "warning"
                                      }
                                    >
                                      {leave.status?.toUpperCase() || "PENDING"}
                                    </Badge>
                                  </h6>
                                  <p className="mb-1 small">{leave.reason}</p>
                                  <small className="text-muted">
                                    {leave.startDate && leave.endDate
                                      ? (() => {
                                          try {
                                            const start = leave.startDate.toDate
                                              ? leave.startDate
                                                  .toDate()
                                                  .toLocaleDateString()
                                              : new Date(
                                                  leave.startDate
                                                ).toLocaleDateString();
                                            const end = leave.endDate.toDate
                                              ? leave.endDate
                                                  .toDate()
                                                  .toLocaleDateString()
                                              : new Date(
                                                  leave.endDate
                                                ).toLocaleDateString();
                                            return `${start} - ${end}`;
                                          } catch (error) {
                                            return "Date range not available";
                                          }
                                        })()
                                      : "Date range not available"}
                                  </small>
                                </div>
                                <div className="col-md-4 text-end">
                                  <small className="text-muted">
                                    Duration: {leave.duration || 0} day(s)
                                  </small>
                                </div>
                              </div>
                              {leave.adminComments && (
                                <div className="mt-2 p-2 bg-light rounded">
                                  <small>
                                    <strong>Admin Comments:</strong>{" "}
                                    {leave.adminComments}
                                  </small>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </div>
          </Tab>

          <Tab
            eventKey="classes"
            title={`🏫 Classes (${classes ? classes.length : 0})`}
          >
            <Card className="shadow">
              <Card.Body>
                <Card.Title>
                  🏫 Classes ({classes ? classes.length : 0})
                </Card.Title>
                {!classes || classes.length === 0 ? (
                  <p className="text-muted text-center">No classes enrolled.</p>
                ) : (
                  <div className="row">
                    {classes.map((classItem) => (
                      <div key={classItem.id} className="col-md-6 mb-3">
                        <Card className="h-100">
                          <Card.Body>
                            <Card.Title className="h6">
                              {classItem.title || classItem.name}
                            </Card.Title>
                            <p className="text-muted small">
                              {classItem.description}
                            </p>
                            <div className="mb-2">
                              <Badge
                                bg={
                                  classItem.status === "active"
                                    ? "success"
                                    : "secondary"
                                }
                              >
                                {classItem.status?.toUpperCase() || "ACTIVE"}
                              </Badge>
                            </div>
                            {classItem.schedule && (
                              <small className="text-muted">
                                Schedule: {classItem.schedule}
                              </small>
                            )}
                            {classItem.instructor && (
                              <div>
                                <small className="text-muted">
                                  Instructor: {classItem.instructor}
                                </small>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
